import { baseApi, resourceListTag, resourceScopeTag } from '@krp/client-runtime'

import { AQSI_TAG_TYPES, AQSI_TAGS } from '../../../shared/api/tags'
import { AQSI_URLS } from '../../../shared/api/urls'
import { decodeGoodsPage } from '../../catalog/api/contracts'
import type { AqsiGood, AqsiGoodsPage } from '../../catalog/model/types'
import type { CommonDeliverResult, CommonDeliveryReasons, CommonGoodHit, CommonGoodsPage, CommonSearchMode, CommonSearchPage } from '../model/commonSearch'
import { decodeCommonDeliverResult, decodeCommonDeliveryReasons, decodeCommonSearchPage } from './commonSearchContracts'
import { PLATFORM_SALE_SEARCH_URLS, SYNC_CASH_DELIVERY_URLS } from './urls'

export interface CommonGoodsArgs {
  resourceId: string
  text: string
  mode: CommonSearchMode
  limit: number
}

export interface CommonDeliverArgs {
  operationId: string
  resourceId: string
  productId: string
}

/** Слова, когда служба синхронизации не ответила, почему товара нет в кассе: поиск продажи из-за подсказки не падает. */
const REASONS_UNAVAILABLE_TEXT = 'служба синхронизации не ответила, почему товара нет в кассе'

/** Выдача поиска + карточки кассы по ключам из карты идентичности, в порядке поиска; причины — по товарам без карточки. */
function hitsOf(found: CommonSearchPage, goods: ReadonlyMap<string, AqsiGood>, reasons: CommonDeliveryReasons): CommonGoodsPage {
  return {
    hits: found.items.map(
      (item): CommonGoodHit => ({
        productId: item.productId,
        name: item.name,
        sku: item.sku,
        matchedBarcode: item.matchedBarcode,
        cardKey: item.cardKey,
        good: item.cardKey === null ? null : (goods.get(item.cardKey) ?? null),
        deliveryReason: reasons.get(item.productId) ?? null,
      }),
    ),
    hasMore: found.hasMore,
  }
}

/**
 * Поиск РМК в общем каталоге (тот же, что у окна «Номенклатура»): сначала Platform (`sale-search` — штрихкод и артикул точно из базы, текст —
 * единая служба text-search), затем карточки кассы по найденным ключам (цена, налог, единица для продажи). Второго поиска нет: служба кассы
 * ничего не ищет, а лишь отдаёт карточки снимка по ключам. Сбой любого шага — ошибка запроса, а не «ничего не найдено».
 */
export const commonSearchApi = baseApi.enhanceEndpoints({ addTagTypes: AQSI_TAG_TYPES }).injectEndpoints({
  endpoints: (build) => ({
    aqsiCommonGoods: build.query<CommonGoodsPage, CommonGoodsArgs>({
      queryFn: async ({ resourceId, text, mode, limit }, _api, _extra, transport) => {
        const searched = await transport({
          url: PLATFORM_SALE_SEARCH_URLS.search,
          params: { resourceId, q: text, mode, limit },
          decode: decodeCommonSearchPage,
        })
        if (searched.error !== undefined) return { error: searched.error }
        const found = searched.data as CommonSearchPage
        // Почему товаров нет в кассе — у службы синхронизации (RP-SYN13); её сбой — слова об этом в подсказке, а не провал поиска продажи.
        const absent = found.items.flatMap((item) => (item.cardKey === null ? [item.productId] : []))
        let reasons: CommonDeliveryReasons = new Map()
        if (absent.length > 0) {
          const answered = await transport({
            url: SYNC_CASH_DELIVERY_URLS.reasons,
            method: 'POST',
            body: { resourceId, productIds: absent },
            readOnly: true,
            decode: decodeCommonDeliveryReasons,
          })
          reasons = answered.error === undefined ? (answered.data as CommonDeliveryReasons) : new Map(absent.map((id) => [id, REASONS_UNAVAILABLE_TEXT]))
        }
        const keys = found.items.flatMap((item) => (item.cardKey === null ? [] : [item.cardKey]))
        if (keys.length === 0) return { data: hitsOf(found, new Map(), reasons) }
        const cards = await transport({
          url: AQSI_URLS.goodsByKeys(resourceId),
          method: 'POST',
          body: { keys },
          readOnly: true,
          decode: decodeGoodsPage(resourceId),
        })
        if (cards.error !== undefined) return { error: cards.error }
        const page = cards.data as AqsiGoodsPage
        return { data: hitsOf(found, new Map(page.items.map((good) => [good.goodId, good])), reasons) }
      },
      providesTags: (_result, _error, { resourceId }) => [resourceListTag(AQSI_TAGS.goods, resourceId), resourceScopeTag(resourceId)],
    }),
    /**
     * «Отправить в кассу сейчас»: создание карточки ОДНОГО общего товара в этой кассе задачей службы синхронизации (RP-SYN13; право `session.open`
     * на кассе). Ключ операции — на нажатие: повтор после обрыва ответа служба узнаёт и отдаёт ту же задачу.
     */
    aqsiCommonDeliver: build.mutation<CommonDeliverResult, CommonDeliverArgs>({
      query: ({ operationId, resourceId, productId }) => ({
        url: SYNC_CASH_DELIVERY_URLS.deliver,
        method: 'POST',
        body: { operationId, resourceId, productId },
        idempotent: true,
        decode: decodeCommonDeliverResult,
      }),
    }),
    /** Ход отправки в кассу: отказ кассы — её слова дословно, без ожидания срока. */
    aqsiCommonDeliveryJob: build.query<CommonDeliverResult, { jobId: string }>({
      query: ({ jobId }) => ({ url: SYNC_CASH_DELIVERY_URLS.job(jobId), decode: decodeCommonDeliverResult }),
    }),
  }),
})

export const { aqsiCommonGoods, aqsiCommonDeliver, aqsiCommonDeliveryJob } = commonSearchApi.endpoints
